import { TweenLite } from 'gsap';
import {isTouchScreen} from './helpers';

/**
 * Class to handle custom cursors.
 */
class CustomCursors {

  constructor() {
    this.x = 0;
    this.y = 0;
    this.cursorType = 'default';
    this.outerCircleSpeed = 0.2;
    this.lastMouseMove = false;
    this.enabled = false;

    // Create cursor elements
    const innerCircle = document.createElement('div');
    innerCircle.classList.add('custom-cursor-inner-circle');
    document.body.appendChild(innerCircle);

    const textElement = document.createElement('span');
    textElement.classList.add('custom-cursor-text');
    textElement.innerText = window.LOCALIZATIONS.CURSOR_VIEW;
    innerCircle.appendChild(textElement);

    const outerCircle = document.createElement('div');
    outerCircle.classList.add('custom-cursor-outer-circle');
    const outerCircleInner = document.createElement('div');
    outerCircleInner.classList.add('custom-cursor-outer-circle-inner');
    outerCircle.appendChild(outerCircleInner);
    document.body.appendChild(outerCircle);

    this._innerCircle = innerCircle;
    this._outerCircle = outerCircle;

    this._mouseDownListener = this._onMouseDown.bind(this);
    this._mouseMoveListener = this._onMouseMove.bind(this);
    this._mouseUpListener = this._onMouseUp.bind(this);
    const enableOnMouseMove = () => {
      document.removeEventListener('mousemove', enableOnMouseMove);
      this.update();
    };
    document.addEventListener('mousemove', enableOnMouseMove);
    window.addEventListener('resize', this._onResize.bind(this));
  }

  _onMouseMove(e) {
    this.x = event.clientX;
    this.y = event.clientY;
    this.lastMouseMove = Date.now();
    this.setCursorType(this.getElementCursorType(e.target));
  }

  _onMouseUp(e) {
    /*this._innerCircle.classList.remove('hold');
    this._outerCircle.classList.remove('hold');*/
  }

  _onMouseDown(e) {
    this._innerCircle.classList.add('clicked');
    this._outerCircle.classList.add('clicked');
    setTimeout(() => {
      this._innerCircle.classList.remove('clicked');
      this._outerCircle.classList.remove('clicked');
    }, 150);

    /*this._innerCircle.classList.add('hold');
    this._outerCircle.classList.add('hold');*/
  }

  _onResize(e) {
    this.update();
  }

  /**
   * Check whether custom cursors are currently supported.
   *
   * @return {Boolean} True if custom cursors are supported.
   */
  isSupported() {
    return !isTouchScreen() && window.innerWidth >= 768;
  }

  /**
   * Get cursor type for given element. If element or none of it's parents have data-custom-cursor attribute, default cursor is used.
   *
   * @param {Node} element Element to check.
   * @return {String} Cursor type.
   */
  getElementCursorType(element) {
    if (element.getAttribute('data-custom-cursor')) {
      return element.getAttribute('data-custom-cursor');
    }
    else if (!element.parentElement || element.parentElement.tagName === 'BODY') {
      return 'default';
    }

    return this.getElementCursorType(element.parentElement);
  }

  /**
   * Set cursor type for given elements.
   *
   * @param {String} type Cursor type.
   * @param {NodeList|String} elements List of HTMLElements or selector string.
   */
  setElementCursorType(type, elements) {
    if (typeof elements === 'string') {
      elements = document.querySelectorAll(elements);
    }

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      element.setAttribute('data-custom-cursor', type);
    }
  }

  /**
   * Set cursor types for given elements.
   *
   * @param {Object} types Cursor type mapped to array of selectors which will be using the cursor type.
   */
  setElementCursorTypes(types) {
    if (typeof types === 'object') {
      for (let type in types) {
        types[type].forEach(selector => {
          this.setElementCursorType(type, selector);
        });
      }
    }
  }

  /**
   * Removes custom cursor type from given element.
   *
   * @param {Node} element HTML element.
   */
  removeElementCursorType(element) {
    if (element) {
      element.removeAttribute('data-custom-cursor');
    }
  }

  /**
   * Set active cursor type.
   *
   * @param {String} type New cursor type.
   */
  setCursorType(type) {
    const oldType = this.cursorType;
    if (type !== oldType) {
      this.cursorType = type;

      if (type === 'year' || type === 'point') {
        this.outerCircleSpeed = 0;
      }
      else {
        this.outerCircleSpeed = 0.1;
      }

      this._outerCircle.classList.remove('cursor-type-' + oldType);
      this._outerCircle.classList.add('cursor-type-' + type);
      this._innerCircle.classList.remove('cursor-type-' + oldType);
      this._innerCircle.classList.add('cursor-type-' + type);
    }
  }

  /**
   * Render custom cursor.
   *
   * @param {DOMHighResTimeStamp} time Time when execution started.
   */
  render(time) {
    if (!this.isSupported()) {
      return;
    }

    const innerCursorBox = this._innerCircle.getBoundingClientRect();
    const outerCursorBox = this._outerCircle.getBoundingClientRect();

    TweenLite.to(this._innerCircle, 0, {
      x: this.x - innerCursorBox.width / 2,
      y: this.y - innerCursorBox.height / 2,
    });

    TweenLite.to(this._outerCircle, this.outerCircleSpeed, {
      x: this.x - outerCursorBox.width / 2,
      y: this.y - outerCursorBox.height / 2
    });
    requestAnimationFrame(this.render.bind(this));
  }

  /**
   * Enables or disables custom cursor and renders cursor.
   */
  update() {
    if (this.isSupported()) {
      this.enable();
      this.render(window.performance.now());
    }
    else {
      this.disable();
    }
  }

  /**
   * Enables custom cursors.
   */
  enable() {
    if (this.enabled) {
      return;
    }
    this.enabled = true;
    document.addEventListener('dragend', this._mouseUpListener);
    document.addEventListener('mouseup', this._mouseUpListener);
    document.addEventListener('mousedown', this._mouseDownListener);
    document.addEventListener('mousemove', this._mouseMoveListener);
    document.body.classList.add('has-custom-cursor');
  }

  /**
   * Disables custom cursors.
   */
  disable() {
    if (!this.enabled) {
      return;
    }
    this.enabled = false;
    document.removeEventListener('dragend', this._mouseUpListener);
    document.removeEventListener('mouseup', this._mouseUpListener);
    document.removeEventListener('mousedown', this._mouseDownListener);
    document.removeEventListener('mousemove', this._mouseMoveListener);
    document.body.classList.remove('has-custom-cursor');
  }
}

module.exports = new CustomCursors();
